import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Command } from "cmdk";
import { toast } from "sonner";
import {
  ArrowRightLeft,
  BarChart3,
  FileText,
  Lightbulb,
  MessageCircle,
  Search,
  Sparkles,
  Zap,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useUiStore } from "@/stores/ui-store";
import { useAssistantStore } from "@/stores/assistant-store";
import { useBrands } from "@/hooks/use-brands";
import { useSearch } from "@/hooks/use-search";
import { useGlobalHotkey } from "@/hooks/use-global-hotkey";
import { analyticsApi } from "@/hooks/use-analytics";
import { PAGE_REGISTRY, subRouteOf } from "@/lib/page-registry";

const ITEM_CLASS =
  "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-foreground data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground";
const GROUP_CLASS =
  "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground";

/**
 * ⌘K command palette (spec §3): pages, brand switching (sub-route
 * preserving), quick actions, cross-entity search (lazy, min 2 chars,
 * server-side via Task 1's endpoint) and the "Ask Pilot" handoff.
 * Mounted in AppLayout — works on /dashboard too; brand-scoped groups
 * appear only when the URL resolves to a brand.
 */
export function CommandPalette() {
  const { t } = useTranslation("palette");
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const paletteOpen = useUiStore((s) => s.paletteOpen);
  const setPaletteOpen = useUiStore((s) => s.setPaletteOpen);
  const openPanel = useAssistantStore((s) => s.openPanel);

  const [query, setQuery] = useState("");

  const { data: brands } = useBrands();
  const slugSegment = pathname.split("/")[1] ?? "";
  const currentBrand = brands?.find((b) => b.slug === slugSegment);
  const slug = currentBrand?.slug;
  const brandId = currentBrand?.id;
  // Mirrors usePermissions().canTriggerSync (sync-status-header) — the
  // palette renders outside BrandProvider, so derive from the brand list.
  const canTriggerSync = currentBrand?.role === "ADMIN";

  useGlobalHotkey("mod+k", () =>
    setPaletteOpen(!useUiStore.getState().paletteOpen),
  );

  // Lazy: no fetch until the palette is open AND the query has 2+ chars.
  const searchQuery = useSearch(paletteOpen ? brandId : undefined, query);
  const results = searchQuery.data;
  const trimmedQuery = query.trim();

  const close = () => {
    setPaletteOpen(false);
    setQuery("");
  };
  const go = (to: string) => {
    navigate(to);
    close();
  };

  const pages = PAGE_REGISTRY.filter((p) => (p.brandScoped ? !!slug : true));
  const otherBrands = (brands ?? []).filter((b) => b.id !== currentBrand?.id);

  const switchBrand = (targetSlug: string) => {
    const sub = slug ? subRouteOf(pathname, slug) : null;
    go(sub ? `/${targetSlug}/${sub}` : `/${targetSlug}`);
  };

  const runAnalyticsSync = () => {
    if (!brandId) return;
    close();
    analyticsApi
      .triggerSync(brandId)
      .then(() => toast.success(t("toast.syncStarted")))
      .catch(() => toast.error(t("toast.syncFailed")));
  };

  const askPilot = () => {
    close();
    openPanel({ prefill: trimmedQuery });
  };

  return (
    <Dialog open={paletteOpen} onOpenChange={(v) => (v ? setPaletteOpen(true) : close())}>
      <DialogContent
        className="overflow-hidden p-0 max-w-xl top-[18%] translate-y-0 gap-0"
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">{t("title")}</DialogTitle>
        <Command loop className="bg-popover text-popover-foreground">
          <div className="flex items-center gap-2 border-b border-border px-3">
            <Search size={14} className="text-muted-foreground flex-shrink-0" />
            <Command.Input
              value={query}
              onValueChange={setQuery}
              placeholder={t("searchPlaceholder")}
              className="h-11 w-full bg-transparent text-[13px] outline-none placeholder:text-muted-foreground"
            />
            <kbd className="rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
              ⌘K
            </kbd>
          </div>
          <Command.List className="max-h-[360px] overflow-y-auto p-1.5">
            <Command.Empty className="py-6 text-center text-[12px] text-muted-foreground">
              {t("noResults")}
            </Command.Empty>

            <Command.Group heading={t("groups.pages")} className={GROUP_CLASS}>
              {pages.map((p) => (
                <Command.Item
                  key={p.id}
                  value={`${p.label} ${p.keywords.join(" ")}`}
                  className={ITEM_CLASS}
                  onSelect={() => go(p.path(slug ?? ""))}
                >
                  <FileText size={13} className="text-muted-foreground" />
                  {p.label}
                </Command.Item>
              ))}
            </Command.Group>

            {otherBrands.length > 0 && (
              <Command.Group heading={t("groups.brands")} className={GROUP_CLASS}>
                {otherBrands.map((b) => (
                  <Command.Item
                    key={b.id}
                    value={`${b.name} ${b.slug} switch brand`}
                    className={ITEM_CLASS}
                    onSelect={() => switchBrand(b.slug)}
                  >
                    <ArrowRightLeft size={13} className="text-muted-foreground" />
                    {b.name}
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {slug && (
              <Command.Group heading={t("groups.actions")} className={GROUP_CLASS}>
                <Command.Item
                  value="new quick post create publish"
                  className={ITEM_CLASS}
                  onSelect={() => go(`/${slug}/quick-post`)}
                >
                  <Zap size={13} className="text-muted-foreground" />
                  {t("actions.newQuickPost")}
                </Command.Item>
                <Command.Item
                  value="open assistant pilot chat ai"
                  className={ITEM_CLASS}
                  onSelect={() => {
                    close();
                    openPanel();
                  }}
                >
                  <Sparkles size={13} className="text-muted-foreground" />
                  {t("actions.openAssistant")}
                </Command.Item>
                {canTriggerSync && (
                  <Command.Item
                    value="run analytics sync refresh metrics"
                    className={ITEM_CLASS}
                    onSelect={runAnalyticsSync}
                  >
                    <BarChart3 size={13} className="text-muted-foreground" />
                    {t("actions.runAnalyticsSync")}
                  </Command.Item>
                )}
              </Command.Group>
            )}

            {/* Server search results: values embed the raw query so cmdk's
                client-side filter never drops them. */}
            {slug && !!results?.pieces.length && (
              <Command.Group heading={t("groups.pieces")} className={GROUP_CLASS}>
                {results.pieces.map((p) => (
                  <Command.Item
                    key={p.id}
                    value={`${trimmedQuery} piece ${p.id}`}
                    className={ITEM_CLASS}
                    onSelect={() =>
                      go(
                        p.shortCode
                          ? `/${slug}/pieces/${p.shortCode}`
                          : `/${slug}/review`,
                      )
                    }
                  >
                    <FileText size={13} className="text-muted-foreground" />
                    <span className="truncate">
                      {p.shortCode}
                      {p.snippet ? ` · ${p.snippet}` : ""}
                    </span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}
            {slug && !!results?.ideas.length && (
              <Command.Group heading={t("groups.ideas")} className={GROUP_CLASS}>
                {results.ideas.map((i) => (
                  <Command.Item
                    key={i.id}
                    value={`${trimmedQuery} idea ${i.id}`}
                    className={ITEM_CLASS}
                    onSelect={() => go(`/${slug}/ideas`)}
                  >
                    <Lightbulb size={13} className="text-muted-foreground" />
                    <span className="truncate">{i.title}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}
            {slug && !!results?.conversations.length && (
              <Command.Group heading={t("groups.conversations")} className={GROUP_CLASS}>
                {results.conversations.map((c) => (
                  <Command.Item
                    key={c.id}
                    value={`${trimmedQuery} conversation ${c.id}`}
                    className={ITEM_CLASS}
                    onSelect={() => go(`/${slug}/inbox/dms/${c.id}`)}
                  >
                    <MessageCircle size={13} className="text-muted-foreground" />
                    <span className="truncate">{c.participantName ?? "Conversation"}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Tail: always last while the input is non-empty. */}
            {trimmedQuery.length > 0 && slug && (
              <Command.Group className={GROUP_CLASS}>
                <Command.Item
                  value={`${trimmedQuery} ask pilot`}
                  className={ITEM_CLASS}
                  onSelect={askPilot}
                >
                  <Sparkles size={13} className="text-primary" />
                  <span className="truncate">{t("actions.askPilot", { query: trimmedQuery })}</span>
                </Command.Item>
              </Command.Group>
            )}
          </Command.List>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
